import axios from "axios";
import { HTTP, CERT } from "@/services/http-common";
import { baseURL } from "../config/setting";

    // 👇 Helpers (colócalos encima del export, en este mismo archivo)
    function b64ToPem(b64) {
      const clean = (b64 || '').replace(/\s+/g, '');
      const chunks = clean.match(/.{1,64}/g) || [];
      return `-----BEGIN CERTIFICATE-----\n${chunks.join('\n')}\n-----END CERTIFICATE-----`;
    }

    function splitB64List(raw) {
      let s = typeof raw === 'string' ? raw.trim() : '';
      // por si llega entre comillas: "b64;b64;..."
      if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1);
      return s
        .split(';')
        .map(x => x.trim())
        .filter(Boolean);
    }

    // evita duplicados preservando orden
    function uniq(list) {
      const s = new Set(); const out = [];
      for (const x of list) if (x && !s.has(x)) { s.add(x); out.push(x); }
      return out;
    }

    // busca valor de atributo en el DN (acepta alias y OID)
    function getDNAttr(dn) {
      if (!dn) return null;
      //const pattern = String(names).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // no usado aquí
      const regex = new RegExp(
        `(?:^|,)\\s*(?:serialNumber|SERIALNUMBER|2\\.5\\.4\\.5|OID\\.2\\.5\\.4\\.5)\\s*=\\s*([^,]+)`,
        'i'
      );
      const m = String(dn).match(regex);
      return m ? m[1].trim() : null;
    }

    // último token tras guion. Si numberOnly, exige solo dígitos
    function lastTokenAfterHyphen(str, numberOnly = false) {
      if (!str) return null;
      const parts = String(str).split('-').map(s => s.trim()).filter(Boolean);
      for (let i = parts.length - 1; i >= 0; i--) {
        const t = parts[i];
        if (!numberOnly || /^\d+$/.test(t)) return t;
      }
      return null;
    }

    // heurística por si no hay atributo: IDxx-XXXX o DNI/NIF
    function guessSerialFromText(s) {
      if (!s) return null;
      // IDESP/IDCES/IDCON/IDCIF/CIF/NIF...-ALFANUM
      let m = s.match(/\bID(?:ESP|CES|CON|CIF)?-([A-Z0-9]+)\b/i);
      if (m) return m[1].toUpperCase();
      // DNI/NIF típico 7-8 dígitos + letra
      m = s.match(/\b(\d{7,8}[A-Z])\b/i);
      if (m) return m[1].toUpperCase();
      return null;
    }

    // === EXTRACTOR PRINCIPAL ===
    function extractIds(cert, opts = {}) {
      const {
        prefer = 'cn',       // prioridad: 'cn' o 'serial'
        numberOnly = false,  // exigir solo dígitos para el serial
        normalize = true,    // quitar espacios y a MAYÚSCULAS
      } = opts;

      const norm = (v) => {
        if (!v) return null;
        const s = String(v);
        return normalize ? s.replace(/\s+/g, '').toUpperCase() : s;
      };

      const subjectCN = (cert?.subjectCN || '').trim();
      const subjectDN = (cert?.subject || '').trim();

      // Regla 1: CN con "R:"
      let cnId = null;
      const mCN = subjectCN.match(/R\s*:\s*([A-Za-z0-9._/-]+)/i);
      if (mCN) cnId = mCN[1];

      // Regla 2: SerialNumber en el DN (alias/oid)
      const serialValue = getDNAttr(subjectDN, ['serialNumber', '2.5.4.5']);
      let serialId = lastTokenAfterHyphen(serialValue, numberOnly);

      // Fallback heurístico si no hay atributo
      if (!serialId) {
        serialId = guessSerialFromText(subjectDN) || guessSerialFromText(subjectCN);
      }

      // normaliza
      cnId = norm(cnId);
      serialId = norm(serialId);

      const ids = uniq([cnId, serialId]);
      const primaryId = (prefer === 'serial' ? (serialId || cnId) : (cnId || serialId)) || null;

      return { ids, cnId, serialId, primaryId, serialSource: serialValue || '(heurística)' };
    }



export const SignaturesService = {
  // GET https://localhost:9999/ping (vía proxy /cert/ping)
    pingCert(timeoutMs = 2000) {
      return CERT.get('/ping', { timeout: timeoutMs });
    },

    // (opcional) llamada directa sin proxy: solo si CORS+cert están OK
    pingCertDirect(timeoutMs = 2000) {
      return axios.get('https://localhost:9999/ping', { timeout: timeoutMs });
    },

    /**
       * GET /getCertificates  → "<b64>;<b64>;..."
       * Devuelve { status, list, raw } donde list es un array de b64.
       */
      async getCertificatesRaw(timeoutMs = 4000) {
        const res = await CERT.get('/getCertificates', {
          timeout: timeoutMs,
          responseType: 'text',
          transformResponse: [(d) => d], // no parsear como JSON
          headers: { Accept: 'text/plain' },
        });

        const raw = res && typeof res.data === 'string' ? res.data : '';
        const list = splitB64List(raw);

        return { status: res.status, list, raw };
      },

      /**
         * Igual que getCertificatesRaw(), pero intenta parsear cada cert
         * y devuelve objetos listos para tu UI.
         * Si NO tienes instalada @peculiar/x509, te devuelve mínimos.
         *
         * Recomendado: npm i @peculiar/x509
         */
        async getCertificatesSummaries(timeoutMs = 4000) {
          const { status, list, raw } = await this.getCertificatesRaw(timeoutMs);

          // si no hay nada, devolvemos vacío
          if (!list.length) {
            return { status, certificates: [], raw };
          }

          try {
            // carga perezosa: no engorda el bundle si no se usa
            const { X509Certificate } = await import(/* webpackChunkName: "x509" */ '@peculiar/x509');

            const certificates = list.map((b64, i) => {
              const pem = b64ToPem(b64);
              const x = new X509Certificate(pem);

              const subject = x.subject;  // "CN=..., O=..., C=..."
              const issuer  = x.issuer;   // "CN=..., O=..., ..."
              const notAfter = new Date(x.notAfter);
              const cnMatch = /(^|,)\s*CN=([^,]+)/i.exec(subject);
              const subjectCN = cnMatch ? cnMatch[2].trim() : subject;

              return {
                id: `cert-${i}`,
                b64,
                pem,
                subject,
                subjectCN,
                issuer,
                notAfter,
                status: (notAfter && notAfter.getTime() < Date.now()) ? 'invalid' : 'valid',
                type: null,
              };
            });

            return { status, certificates, raw };
          } catch (e) {
            // Fallback sin @peculiar/x509 instalada
            const certificates = list.map((b64, i) => ({
              id: `cert-${i}`,
              b64,
              pem: b64ToPem(b64),
              subject: '(sin parsear)',
              subjectCN: '(sin parsear)',
              issuer: '(sin parsear)',
              notAfter: null,
              status: 'valid',
              type: null,
            }));
            return { status, certificates, raw, warn: 'x509-parser-missing' };
          }
        },

        extractCertificateIds(cert, options) {
          return extractIds(cert, options);
        },



  authCheck(id, token, authId) {
      console.log("Dentro de authCheck con authId: " + authId);
      let url = `${baseURL}signatures/${id}/`;

      // Solo agregar authid si está definido y no es nulo
      if (authId) {
        url += `?authid=${authId}`;
      }
    return axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
        Accept: "application/json",
      },
    });
  },

  getSignatureRequestDataAuth(id, citizenId, isAuthCitizenId = false, token = null) {
    if (isAuthCitizenId) {
      return HTTP.get(`signatures/${id}/?authid=${citizenId}`);
    }else{
      return axios.get(baseURL + `signatures/${id}/?authid=${citizenId}`, {
        headers: {
          Authorization: `Token ${token}`,
          Accept: "application/json",
        },
      });
    }
  },

getSignatureRequestDocument(id) {
    let url = `signatures/${id}/document/`;

    // Obtener token de localStorage según prioridad
    let token = localStorage.getItem("auth_user_token") || localStorage.getItem("access_token") || null;
    console.log("Token: " + token);

    // Obtener authId solo si está presente

    let authId = localStorage.getItem("auth_id");
    if(!authId) authId="";
    console.log("authId: ", authId);
    if (authId) {
        url += `?authid=${authId}`;
    }

    // Si no hay token, evitar enviar la autorización
    let headers = {
        Accept: "application/pdf",
    };

    if (token) {
        headers.Authorization = `Token ${token}`;
    }

    return HTTP.get(url, {
        responseType: "arraybuffer",
        headers: headers,
    });
},

getReqEvidencesDocument(id) {
    let url = `signatures/${id}/evidences/`;

    // Obtener token de localStorage según prioridad
    let token = localStorage.getItem("auth_user_token") || localStorage.getItem("access_token") || null;
    console.log("Token: " + token);

    // Obtener authId solo si está presente

    let authId = localStorage.getItem("auth_id");
    if(!authId) authId="";
    console.log("authId: ", authId);
    if (authId) {
        url += `?authid=${authId}`;
    }

    // Si no hay token, evitar enviar la autorización
    let headers = {
        Accept: "application/pdf",
    };

    if (token) {
        headers.Authorization = `Token ${token}`;
    }

    return HTTP.get(url, {
        responseType: "arraybuffer",
        headers: headers,
    });
},

  sendOtp(requestId, data, token) {
    console.log ("data" + data);
    console.log ("this.token" + token);
    return axios.post(`${baseURL}signatures/${requestId}/sign/`, data, {
      headers: {
        Authorization: `Token ${token}`,
        Accept: "application/json",
      },
    });
  
  },


sendMultipleOtp(token) {
    console.log("Enviando código OTP para firma múltiple con token: " + token)
    return axios.post(`${baseURL}signatures/otp/`, null, { // 👈 Enviamos `{}` si no hay body
        headers: {
            Authorization: `Token ${token}`,
//            Accept: "application/json",
//            "Content-Type": "application/json", // 👈 Agregar esto si el servidor lo requiere
        },
    });
},

sendUserOtp(token, ids) {
  // 1. Log para depuración
  console.log("Enviando código OTP para firma múltiple con token:", token);
  console.log("IDs a incluir en la URL:", ids);

  // 2. Construir el query string con múltiples parámetros "id"
  //    Usamos URLSearchParams para que haga el encode automáticamente.
  const params = new URLSearchParams();
  ids.forEach(id => {
    params.append("id", id);
  });
  //    Ejemplo de params.toString(): "id=123&id=456&id=789"

  // 3. Formar la URL final
  const url = `${baseURL}signatures/otp/?${params.toString()}`;
  console.log("URL: " + url);

  // 4. Hacer el POST sin body (null) y enviar el token en headers
  return axios.post(url, null, {
    headers: {
      Authorization: `Token ${token}`,
      // Si tu servidor exige JSON explícitamente, podrías descomentar:
      // "Accept": "application/json",
      // "Content-Type": "application/json",
    },
  });
},


  sendOtpOneTimeAuth(requestId, data, token, authId){
    return axios.post(`${baseURL}signatures/${requestId}/sign/?authid=${authId}`, data, {
      headers: {
        Authorization: `Token ${token}`,
        Accept: "application/json",
      },
    });
  },


  signDocument(requestId, data, ip) {
    const config = {};
    console.log("IP dentro de signDocument: "+ ip);
      if (ip) {
        config.headers = { 'x-forwarded-for': ip };
      }
      return HTTP.post(`signatures/${requestId}/sign/`, data, config);
  },

  rejectDocument(requestId, data) {
    return HTTP.post(`signatures/${requestId}/reject/`, data);
  },
/**
/**
  getSignatureRequestData(id, authId = null) {
    return HTTP.get(`signatures/${id}/?authid=${authId}`);
  },
**/
  getSignatureRequestData(id, authId = null) {
      let url = `signatures/${id}/`;

      // Solo agrega authid si tiene un valor válido
      if (authId) {
          url += `?authid=${authId}`;
      }
      return HTTP.get(url);
  },

  login(username, password) {
    const token = `${username}:${password}`;
    const encodedToken = Buffer.from(token, "utf8").toString("base64");
    return axios.get(baseURL + `users/${username}/`, {
      headers: {
        Authorization: `Basic ${encodedToken}`,
        Accept: "application/json",
      },
    });
  },

  signCertificateSignaturesDocument(apiToken, data, signatureId) {
    return axios.post(`${baseURL}signatures/${signatureId}/sign/`, data, {
      headers: {
        Authorization: `Token ${apiToken}`,
        Accept: "application/json",
      },
    });
  },

  getCertificateSignatureRequests(token, params) {
    return axios.get(baseURL + `signatures/`, {
      params: params,
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  },

getCertificateSignatureRequestDocument(requestId, token, authId) {
    // Definir la URL base
    let url = `${baseURL}signatures/${requestId}/document/`;

    // Agregar authId solo si tiene un valor válido
    if (authId) {
        url += `?authid=${authId}`;
    }

    // Hacer la petición con la URL correcta
    return axios.get(url, {
        responseType: "arraybuffer",
        headers: {
            Authorization: `Token ${token}`,
            Accept: "application/pdf",
        },
    });
},

    getCertificateSignatureRequestDocumentUser(requestId, token) {
      return axios.get(
        baseURL + `signatures/${requestId}/`,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: `Token ${token}`,
            Accept: "application/pdf",
          },
        }
      );
    },

  rejectCertificateDocument(apiToken, data, signatureId) {
    return axios.post(baseURL + `signatures/${signatureId}/reject/`, data, {
      headers: {
        Authorization: `Token ${apiToken}`,
        Accept: "application/json",
      },
    });
  },

  getCertificateSignatureDocument(apiToken, signatureId, authId = null) {
    let data = null;
    let url = `${baseURL}signatures/${signatureId}/sign/`;
    if (authId!=null) {
      url = url + `?authId=${authId}`;
    }

    return axios.get(url, data, {
      headers: {
        Authorization: `Token ${apiToken}`,
        Accept: "application/json",
      },
    });
  },
  getBatchCertificateSignatureRequests(token, authId) {
    return axios.get( `${baseURL}signatures/?authId=${authId}`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  },

  downloadEvidenceReport(id) {
    return HTTP.get(`evr/${id}/`, {
      responseType: "arraybuffer",
      headers: { Accept: "application/pdf" },
    });
  },
};
